import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";

const bg =
  "https://static.prod-images.emergentagent.com/jobs/a325f092-209a-455a-9db8-7e05bc028647/images/34fab784368a9d27f615540ac81666366ae1f7032305d26fdde1133355c00544.png";

export default function Diagnostic() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0); // 0..5 = questions, 6 = open
  const [answers, setAnswers] = useState({});
  const [openAnswer, setOpenAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/questions").then((r) => setQuestions(r.data.questions || []));
  }, []);

  const total = questions.length + 1; // 7
  const progress = Math.min(100, Math.round(((step + 1) / total) * 100));

  const currentQ = questions[step];

  function handleAnswer(letter) {
    const qid = String(currentQ.id);
    setAnswers((prev) => ({ ...prev, [qid]: letter }));
    setTimeout(() => {
      if (step < questions.length - 1) setStep((s) => s + 1);
      else setStep(questions.length); // move to open question
    }, 280);
  }

  async function handleSubmit() {
    if (openAnswer.trim().length < 5) {
      toast.error("Prenez un instant pour exprimer ce qui vous pèse.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/diagnostic", {
        answers,
        open_answer: openAnswer.trim(),
      });
      sessionStorage.setItem("li_diag_id", res.data.diagnostic_id);
      if (res.data.scores) {
        sessionStorage.setItem("li_diag_scores", JSON.stringify(res.data.scores));
      }
      navigate("/transition");
    } catch (e) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
      setSubmitting(false);
    }
  }

  function goBack() {
    if (step > 0) setStep((s) => s - 1);
  }

  if (!questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="font-serif text-ink-soft animate-breath">Chargement</div>
      </div>
    );
  }

  const isOpenStep = step === questions.length;

  return (
    <div className="relative min-h-screen bg-sand-50" data-testid="diagnostic-page">
      <div
        className="absolute inset-0 -z-10 opacity-[0.12]"
        style={{ backgroundImage: `url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />

      {/* Top bar */}
      <header className="px-6 md:px-12 pt-6 md:pt-8 max-w-3xl mx-auto">
        <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-ink-muted">
          <button
            onClick={goBack}
            disabled={step === 0}
            className="disabled:opacity-30 hover:text-ink transition-colors"
            data-testid="back-btn"
          >
            ← Retour
          </button>
          <span data-testid="progress-label">
            {isOpenStep ? `Question ${total} sur ${total}` : `Question ${step + 1} sur ${total}`}
          </span>
        </div>
        <div className="mt-4 h-[2px] w-full bg-sand-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-sand-500"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="progress-bar"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-20 min-h-[70vh]">
        <AnimatePresence mode="wait">
          {!isOpenStep ? (
            <motion.section
              key={`q-${step}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`question-${step + 1}`}
            >
              <p className="text-[11px] uppercase tracking-[0.4em] text-sand-500 mb-5 flex items-center gap-3">
                <span className="h-px w-8 bg-sand-400" />
                Diagnostic Lumière Intérieure
                <span className="text-sand-400">✦</span>
              </p>
              <h2 className="font-display text-3xl md:text-[2.6rem] leading-[1.1] text-ink max-w-2xl">
                {currentQ.text}
              </h2>

              <div className="mt-12 grid gap-3">
                {Object.entries(currentQ.options).map(([letter, label], i) => {
                  const selected = answers[String(currentQ.id)] === letter;
                  return (
                    <motion.button
                      key={letter}
                      onClick={() => handleAnswer(letter)}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.07, duration: 0.5 }}
                      className={`group w-full text-left rounded-2xl border bg-white/85 hover:bg-white p-5 md:p-6 transition-all duration-500 flex items-center gap-5 ${
                        selected
                          ? "border-sand-500 shadow-soft scale-[1.01]"
                          : "border-sand-200 hover:border-sand-400 hover:-translate-y-[2px] hover:shadow-soft"
                      }`}
                      data-testid={`qcm-option-${currentQ.id}-${letter}`}
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border font-display text-base transition-all duration-300 ${
                          selected
                            ? "bg-sand-500 text-white border-sand-500 scale-110"
                            : "border-sand-300 text-sand-500 group-hover:border-sand-500 group-hover:bg-sand-50"
                        }`}
                      >
                        {letter}
                      </span>
                      <span className="text-base md:text-lg text-ink leading-snug flex-1">{label}</span>
                      <span
                        aria-hidden
                        className={`text-sand-400 transition-all duration-500 ${
                          selected ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 group-hover:opacity-60 group-hover:translate-x-0"
                        }`}
                      >
                        →
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.section>
          ) : (
            <motion.section
              key="open"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.55 }}
              data-testid="open-question"
            >
              <p className="text-[11px] uppercase tracking-[0.4em] text-sand-500 mb-5 flex items-center gap-3">
                <span className="h-px w-8 bg-sand-400" />
                Une dernière question
                <span className="text-sand-400">✦</span>
              </p>
              <h2 className="font-display text-3xl md:text-[2.6rem] leading-[1.1] text-ink max-w-2xl">
                Pour finir, une question plus personnelle.
              </h2>
              <p className="mt-5 text-ink-soft max-w-xl italic text-base">
                Qu'est-ce qui vous pèse le plus aujourd'hui, intérieurement&nbsp;?
                Quelques mots suffisent, ceux qui vous viennent, sans chercher la formulation parfaite.
              </p>

              <textarea
                value={openAnswer}
                onChange={(e) => setOpenAnswer(e.target.value)}
                rows={7}
                placeholder="En quelques mots, ce qui vous traverse en ce moment"
                className="mt-8 w-full rounded-2xl border border-sand-200 bg-white p-5 text-base text-ink placeholder:text-ink-muted/70 focus:outline-none focus:ring-2 focus:ring-sand-300 focus:border-sand-500 transition resize-none shadow-soft/30"
                data-testid="open-answer-textarea"
              />

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 rounded-full bg-sand-500 hover:bg-sand-600 text-white pl-6 pr-2 py-2 shadow-soft transition-all duration-500 hover:shadow-elevated disabled:opacity-50 whitespace-nowrap"
                  data-testid="submit-diagnostic-btn"
                >
                  <span className="text-sm font-medium tracking-wide leading-none">
                    {submitting ? "Envoi en cours" : "Terminer le diagnostic"}
                  </span>
                  <span
                    aria-hidden
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 transition-all duration-500 group-hover:bg-white group-hover:text-sand-500 group-hover:rotate-[-12deg] text-sm"
                  >
                    →
                  </span>
                </button>
                <p className="text-sm text-ink-muted italic">Ce que vous écrivez reste entre vous et Cindy.</p>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
